// Cliente HTTP del SPA. Todas las llamadas son del MISMO ORIGEN con URL relativa
// bajo /api/v1: la cookie de sesión httpOnly viaja sola y la protección CSRF del
// backend (http.CrossOriginProtection, sin token) valida Sec-Fetch-Site/Origin.
// No hay, ni hace falta, ninguna fontanería de tokens CSRF/XSRF.

const API_BASE = "/api/v1";

export type ErrorFields = Record<string, string>;

/**
 * ApiError es la forma tipada de los errores del backend
 * ({ error: { code, message, fields? } }). El SPA enruta/actúa según `code`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: ErrorFields;
  /** Segundos de espera sugeridos por la cabecera Retry-After (429). */
  readonly retryAfter?: number;

  constructor(
    status: number,
    code: string,
    message: string,
    fields?: ErrorFields,
    retryAfter?: number,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.retryAfter = retryAfter;
  }
}

// Interceptor global de sesión caducada. El router lo registra para limpiar la
// caché de /me y redirigir a /login conservando el deep-link.
// F4 (offline): este handler NO debe descartar la outbox de mutaciones pendientes
// — una sesión caducada no equivale a perder el trabajo encolado sin red.
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

type ErrorEnvelope = {
  error?: { code?: string; message?: string; fields?: ErrorFields };
};

async function toApiError(res: Response): Promise<ApiError> {
  let code = "ERROR_DESCONOCIDO";
  let message = "Se produjo un error inesperado.";
  let fields: ErrorFields | undefined;
  try {
    const body = (await res.json()) as ErrorEnvelope;
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      fields = body.error.fields;
    }
  } catch {
    // Respuesta sin cuerpo JSON: se conservan los valores por defecto.
  }

  let retryAfter: number | undefined;
  const header = res.headers.get("Retry-After");
  if (header) {
    const parsed = Number.parseInt(header, 10);
    if (!Number.isNaN(parsed)) {
      retryAfter = parsed;
    }
  }

  return new ApiError(res.status, code, message, fields, retryAfter);
}

// throwIfError centraliza el manejo de respuestas no-2xx: traduce el sobre de error a
// un ApiError tipado y dispara el interceptor de sesión caducada cuando corresponde.
// Solo la pérdida de sesión (NO_AUTENTICADO) redirige; un 401 de login
// (CREDENCIALES_INVALIDAS) NO debe redirigir (lo gestiona la pantalla de login inline).
async function throwIfError(res: Response): Promise<void> {
  if (res.ok) {
    return;
  }
  const error = await toApiError(res);
  if (res.status === 401 && error.code === "NO_AUTENTICADO") {
    unauthorizedHandler?.();
  }
  throw error;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "same-origin",
  };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  } else if (method === "POST" || method === "PUT" || method === "PATCH") {
    // El backend exige Content-Type: application/json en TODA mutación (requireJSON),
    // incluso las que no llevan cuerpo (transiciones de LOTO: ejecutar/verificar/cerrar).
    // Sin esta cabecera responde 415 y la transición nunca se aplica. Enviamos un objeto
    // JSON vacío para satisfacer el gate sin alterar la semántica del endpoint.
    init.headers = { "Content-Type": "application/json" };
    init.body = "{}";
  }

  const res = await fetch(`${API_BASE}${path}`, init);
  await throwIfError(res);

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/**
 * Descarga es un binario recibido del backend junto con el nombre de archivo que el
 * SERVIDOR sugiere en `Content-Disposition` (#249 D7). `nombreArchivo` es opcional: si la
 * cabecera falta o no es interpretable, el caller aplica su propio nombre de respaldo.
 */
export type Descarga = { blob: Blob; nombreArchivo?: string };

/**
 * nombreDeContentDisposition extrae el nombre de archivo de la cabecera
 * `Content-Disposition` que fija el servidor (#249 D7). Acepta las dos formas que emite el
 * backend Go, `filename="x.xlsx"` y `filename=x.xlsx`, y devuelve undefined si la cabecera
 * falta o no trae nombre (el caller aplica entonces su respaldo). Se descarta cualquier
 * nombre con separadores de ruta: un `download` no debe poder salirse del nombre plano.
 *
 * Desde #435 prefiere además `filename*=UTF-8''…` (RFC 5987): la documentación acreditativa
 * conserva su nombre original, que puede no ser ASCII, y el servidor manda en `filename` solo
 * un respaldo degradado. Un `filename*` ilegible o con separadores cae a ese respaldo.
 */
export function nombreDeContentDisposition(cabecera: string | null): string | undefined {
  if (!cabecera) {
    return undefined;
  }
  const extendido = /filename\*\s*=\s*UTF-8''([^;\s]+)/i.exec(cabecera);
  if (extendido?.[1]) {
    const nombre = decodificarRFC5987(extendido[1]);
    if (nombre !== undefined && esNombrePlano(nombre)) {
      return nombre;
    }
  }
  const match = /filename\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(cabecera);
  const nombre = (match?.[1] ?? match?.[2] ?? "").trim();
  if (!esNombrePlano(nombre)) {
    return undefined;
  }
  return nombre;
}

// esNombrePlano descarta lo vacío y cualquier separador de ruta.
function esNombrePlano(nombre: string): boolean {
  return nombre !== "" && !nombre.includes("/") && !nombre.includes("\\");
}

// decodificarRFC5987 deshace el %XX de un ext-value; undefined si la secuencia es inválida.
function decodificarRFC5987(valor: string): string | undefined {
  try {
    return decodeURIComponent(valor).trim();
  } catch {
    return undefined;
  }
}

// requestBlob descarga un binario (p. ej. la exportación xlsx de los maestros, #33). Como
// las mutaciones, viaja con la cookie de sesión del mismo origen y comparte el manejo de
// errores; el caller convierte el Blob en descarga del navegador con el nombre del servidor.
async function requestBlob(path: string): Promise<Descarga> {
  const res = await fetch(`${API_BASE}${path}`, { method: "GET", credentials: "same-origin" });
  await throwIfError(res);
  return {
    blob: await res.blob(),
    nombreArchivo: nombreDeContentDisposition(res.headers.get("Content-Disposition")),
  };
}

// requestForm sube un multipart/form-data (la importación ETL de maestros, #32; con PUT, la
// documentación acreditativa, #435). NO fija Content-Type a propósito: el navegador añade el
// boundary del multipart. Comparte el sobre de error del resto del cliente.
async function requestForm<T>(
  path: string,
  form: FormData,
  method: "POST" | "PUT" = "POST",
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "same-origin",
    body: form,
  });
  await throwIfError(res);

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>("GET", path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>("PATCH", path, body),
  del: <T>(path: string): Promise<T> => request<T>("DELETE", path),
  getBlob: (path: string): Promise<Descarga> => requestBlob(path),
  postForm: <T>(path: string, form: FormData): Promise<T> => requestForm<T>(path, form),
  putForm: <T>(path: string, form: FormData): Promise<T> => requestForm<T>(path, form, "PUT"),
};
